import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, LogOut, Mail } from 'lucide-react';

interface ProducerStatusBlockProps {
  producerStatus: 'paused' | 'banned' | string;
  producerName?: string;
}

/**
 * Full-screen block shown when a producer's business status is paused or banned.
 * Only shown for paused/banned status, not pending (pending can still access portal).
 */
export function ProducerStatusBlock({ producerStatus, producerName }: ProducerStatusBlockProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleGoToFarms = () => {
    navigate('/app/producer');
  };

  const isPaused = producerStatus === 'paused';
  const isBanned = producerStatus === 'banned';

  const title = isBanned 
    ? 'Värdkontot är avstängt' 
    : 'Värdportalen är pausad';
  
  const description = isBanned
    ? 'Ditt värdkonto har stängts av. Detta kan bero på brott mot våra villkor eller på din egen begäran.'
    : 'Ditt värdkonto är tillfälligt pausat. Du kan inte göra ändringar eller ta emot beställningar just nu.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
      <Card className="w-full max-w-lg shadow-xl border-2">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
              isBanned 
                ? 'bg-destructive/10' 
                : 'bg-orange-100'
            }`}>
              <AlertCircle className={`h-7 w-7 ${
                isBanned 
                  ? 'text-destructive' 
                  : 'text-orange-600'
              }`} />
            </div>
          </div>
          <CardTitle className="font-display text-2xl">
            {title}
          </CardTitle>
          {producerName && (
            <p className="text-muted-foreground font-medium mt-1">
              {producerName}
            </p>
          )}
          <CardDescription className="text-base mt-2">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Contact info */}
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Kontakta Farmly om du tror att detta är fel eller vill diskutera din kontostatus.
            </p>
            <a 
              href="mailto:hello@farmly.se" 
              className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
            >
              <Mail className="h-4 w-4" />
              hello@farmly.se
            </a>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-2">
            <Button
              onClick={handleGoToFarms}
              variant="outline"
              className="w-full"
            >
              Tillbaka till mina gårdar
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logga ut
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
